<template>
  <ModalComponent
    :submitButtonText="t('dashboard.portfolio.footer.confirm_send_btn')"
    :hideSubmitButton="true"
    @setForceToggle="setForceToggle"
    @close="close"
    size="lg"
  >
    <template #title>
      {{ t("dashboard.portfolio.footer.review_send_title") }}
    </template>

    <template #body>
      <div class="p-3">
        <h6 class="border-bottom pb-2 mb-3 text-primary">
          {{ t("dashboard.portfolio.sendGoOrderSummary") }}
        </h6>

        <div class="row mb-4">
          <div class="col-md-6">
            <small class="text-muted d-block">{{
              t("dashboard.portfolio.recipientCompanyName")
            }}</small>
            <div class="fw-bold">
              {{ recipientCompanyName }}
              <span v-if="isHostedRecipient" class="badge bg-primary ms-2">{{
                t("hosted_accounts.hosted_account")
              }}</span>
            </div>
          </div>
          <template v-if="!isHostedRecipient">
            <div class="col-md-6">
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.recipient_eecs_account_number")
              }}</small>
              <div class="font-monospace">
                {{ recipientEecsAccountNumber
                }}<span
                  v-if="recipientCountryDisplay"
                  class="text-muted fw-normal"
                >
                  ({{ recipientCountryDisplay }})</span
                >
              </div>
            </div>
            <div
              v-if="recipientRegistryInternalAccountNumber"
              class="col-md-6 mt-2"
            >
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.recipient_internal_account_number")
              }}</small>
              <div class="font-monospace">
                {{ recipientRegistryInternalAccountNumber }}
              </div>
            </div>
            <div v-if="unitPricePln" class="col-md-6 mt-2">
              <small class="text-muted d-block">{{
                t("go_send.unit_price_pln_label")
              }}</small>
              <div class="fw-bold">{{ unitPricePln }}</div>
              <small class="text-muted d-block">{{
                t("go_send.unit_price_pln_help")
              }}</small>
            </div>
          </template>
          <div v-else class="col-12 mt-2">
            <div
              class="alert alert-subtle-secondary d-flex align-items-start gap-2 mb-0"
            >
              <FontAwesomeIcon
                icon="fas fa-circle-info"
                class="mt-1 flex-shrink-0 text-muted"
              />
              <span class="small opacity-75">
                {{ t("go_send.hosted_delivery_info") }}
              </span>
            </div>
          </div>
        </div>

        <h6 class="border-bottom pb-2 mb-3 text-primary">
          {{ t("dashboard.portfolio.footer.technology_amount") }} &
          {{ t("dashboard.portfolio.footer.production_period") }}
        </h6>

        <div class="row">
          <div class="col-md-4">
            <small class="text-muted d-block">{{
              t("dashboard.portfolio.totalMwhToSend")
            }}</small>
            <div class="fw-bold fs-5">{{ totalMwh }} MWh</div>
          </div>
          <div class="col-md-4">
            <small class="text-muted d-block">{{
              t("dashboard.portfolio.technologies")
            }}</small>
            <div>{{ technologies || "-" }}</div>
          </div>
          <div class="col-md-4">
            <small class="text-muted d-block">{{
              t("dashboard.portfolio.production_months")
            }}</small>
            <div>{{ productionMonths || "-" }}</div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <small class="text-muted d-block">{{
              t("dashboard.portfolio.footer.production_support")
            }}</small>
            <div>{{ productionSupport }}</div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-success"
        :disabled="confirmDisabled || isLoading"
        @click="confirm"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("dashboard.portfolio.footer.confirm_send_btn") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

defineProps<{
  recipientCompanyName: string;
  recipientCountryDisplay: string;
  recipientEecsAccountNumber: string;
  recipientRegistryInternalAccountNumber?: string;
  unitPricePln?: string;
  isHostedRecipient: boolean;
  totalMwh: number;
  technologies: string | null;
  productionMonths: string | null;
  productionSupport: string;
  isLoading: boolean;
  confirmDisabled?: boolean;
}>();

const emit = defineEmits<{
  confirm: [];
}>();

const visible = defineModel<boolean>({ required: true });

const { t } = useI18n();

const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

watch(visible, (open) => forceToggle.value?.(open));

const close = () => {
  visible.value = false;
};

const confirm = () => {
  close();
  emit("confirm");
};
</script>
